import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
  useOutsideClick,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaCheck,
  FaSearch,
  FaStar,
} from 'react-icons/fa';
import { searchProspectGmb } from '../services/landingMarketService';
import ProspectMarketAnalysisModal, { useProspectMarketAnalysis } from './ProspectMarketAnalysisModal';

const SEARCH_DEBOUNCE_MS = 450;
const INPUT_HEIGHT = '44px';

const livePulseSx = {
  animation: 'livePulse 1.8s ease-in-out infinite',
  '@keyframes livePulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  },
};

function LiveStatus() {
  return (
    <Flex
      align="center"
      justify="center"
      gap="6px"
      h="100%"
      flexShrink={0}
      pointerEvents="none"
      aria-label="Live"
    >
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg="green.400"
        flexShrink={0}
        sx={livePulseSx}
      />
      <Text
        as="span"
        fontSize="xs"
        fontWeight="600"
        color="green.600"
        letterSpacing="0.02em"
        lineHeight="1"
        m={0}
        position="relative"
        top="1px"
      >
        Live
      </Text>
    </Flex>
  );
}

function BusinessOption({ business, onSelect }) {
  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      px={3}
      py={2}
      bg="white"
      borderRadius="md"
      _hover={{ bg: 'blue.50' }}
      onClick={() => onSelect(business)}
    >
      <HStack justify="space-between" align="center" spacing={2}>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
            {business.name}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {[business.primary_type_display, business.address].filter(Boolean).join(' · ')}
          </Text>
        </Box>
        {business.rating != null && (
          <HStack spacing={0.5} flexShrink={0}>
            <Icon as={FaStar} boxSize={3} color="orange.400" />
            <Text fontSize="xs" fontWeight="600" color="gray.700">
              {business.rating.toFixed(1)}
            </Text>
          </HStack>
        )}
      </HStack>
    </Box>
  );
}

function CompactInput({
  leftIcon,
  rightElement,
  rightElementWidth = '40px',
  isHero,
  ...inputProps
}) {
  const inputBg = isHero ? 'white' : 'gray.50';
  const borderColor = isHero ? 'whiteAlpha.500' : 'gray.200';

  return (
    <InputGroup size="md">
      {leftIcon && (
        <InputLeftElement h={INPUT_HEIGHT} w="40px">
          <Icon as={leftIcon} color="gray.400" boxSize={3.5} />
        </InputLeftElement>
      )}
      <Input
        h={INPUT_HEIGHT}
        minH={INPUT_HEIGHT}
        maxH={INPUT_HEIGHT}
        bg={inputBg}
        color="gray.900"
        borderRadius="md"
        pl={leftIcon ? '40px' : 3}
        pr={rightElement ? rightElementWidth : 3}
        fontSize="16px"
        lineHeight="short"
        border="1px solid"
        borderColor={borderColor}
        _placeholder={{ color: 'gray.400' }}
        _hover={{ borderColor: isHero ? 'whiteAlpha.700' : 'gray.300' }}
        _focus={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
        }}
        {...inputProps}
      />
      {rightElement && (
        <InputRightElement
          h={INPUT_HEIGHT}
          w={rightElementWidth}
          pr={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {rightElement}
        </InputRightElement>
      )}
    </InputGroup>
  );
}

/**
 * Compact landing signup: GMB search then loading analysis with email at the end.
 */
function ProspectGmbSignupForm({
  onSubmit,
  isSubmitting = false,
  error = '',
  variant = 'hero',
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [anchorBusiness, setAnchorBusiness] = useState(null);
  const [resolveError, setResolveError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [retryNeighborhood, setRetryNeighborhood] = useState('');
  const [retryCity, setRetryCity] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const searchWrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const isDesktop = useBreakpointValue({ base: false, md: true });

  const {
    isOpen,
    analysisStep,
    analysisProgress,
    marketResolution,
    marketKeyword,
    competitors,
    startAnalysis,
    closeAnalysis,
  } = useProspectMarketAnalysis();

  const isHero = variant === 'hero';

  useOutsideClick({
    ref: searchWrapperRef,
    handler: () => {
      if (!anchorBusiness) {
        setSearchResults([]);
      }
    },
  });

  const runSearch = useCallback(async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError('');
    try {
      const result = await searchProspectGmb(trimmed, 6);
      setSearchResults(result.businesses || []);
    } catch (err) {
      setSearchResults([]);
      setSearchError(err.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (!anchorBusiness) {
        runSearch(searchQuery);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, anchorBusiness, runSearch]);

  const handleSelectBusiness = (business) => {
    setAnchorBusiness(business);
    setSearchQuery(business.name);
    setSearchResults([]);
    setResolveError('');
    setNotFound(false);
    setRetryNeighborhood('');
    setRetryCity('');
  };

  const runAnalysis = async (hints = {}) => {
    setResolveError('');
    setEmailError('');
    setIsAnalyzing(true);

    try {
      await startAnalysis(anchorBusiness, hints);
      setNotFound(false);
    } catch (err) {
      if (err?.code === 'MARKET_NOT_FOUND') {
        setNotFound(true);
        setResolveError(
          "We couldn't find this business in the top 20 for any local search we tried. Add a neighborhood or city below and we'll search again.",
        );
      } else {
        setResolveError(err.message || 'Could not analyze this market.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyze = async (event) => {
    event.preventDefault();

    if (!anchorBusiness) {
      setSearchError('Select a Google Business Profile');
      return;
    }

    setNotFound(false);
    await runAnalysis();
  };

  const handleRetryWithHints = async () => {
    if (!retryNeighborhood.trim() && !retryCity.trim()) return;
    await runAnalysis({ neighborhood: retryNeighborhood, manualCity: retryCity });
  };

  const handleConfirmReport = async () => {
    if (!marketResolution?.recommended?.query || !anchorBusiness) return;

    if (!email?.trim() || !email.includes('@')) {
      setEmailError('Please enter a valid business email');
      return;
    }

    try {
      await onSubmit({
        email: email.trim(),
        anchorBusiness,
        marketKeyword,
        recommendation: marketResolution.recommended,
        marketLabel: marketResolution.market_label,
        competitors,
      });
      // The report request is queued — hand the page back so it can show its
      // own confirmation (and any optional upgrade offer) below the form.
      closeAnalysis();
    } catch (submitErr) {
      closeAnalysis();
      setResolveError(
        submitErr?.response?.data?.detail ||
          submitErr?.message ||
          'Could not request your report. Please try again.',
      );
    }
  };

  const canAnalyze = Boolean(anchorBusiness && !isAnalyzing && !isSubmitting && !isOpen);
  const showDropdown = (searchResults.length > 0 || searchError) && !anchorBusiness;

  // Trailing status slot: selected → check, searching → spinner, idle → Live.
  let trailingStatus = <LiveStatus />;
  let trailingWidth = '58px';
  if (anchorBusiness) {
    trailingStatus = <Icon as={FaCheck} color="green.500" boxSize={3.5} />;
    trailingWidth = '40px';
  } else if (isSearching) {
    trailingStatus = <Spinner size="sm" color="blue.500" />;
    trailingWidth = '40px';
  }

  const searchInput = (
    <FormControl isRequired flex={1} minW={0}>
      <CompactInput
        isHero={isHero}
        leftIcon={FaSearch}
        rightElement={trailingStatus}
        rightElementWidth={trailingWidth}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setAnchorBusiness(null);
          setResolveError('');
          setSearchError('');
        }}
        placeholder="Search Google Business Profile"
        aria-label="Search Google Business Profile"
      />
    </FormControl>
  );

  // Keep results in document flow so hero overflow:hidden cannot clip them.
  const dropdownPanel = showDropdown ? (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      shadow="lg"
      maxH="220px"
      overflowY="auto"
      w="full"
    >
      {searchError ? (
        <Text px={3} py={2} fontSize="sm" color="red.500">
          {searchError}
        </Text>
      ) : (
        <VStack spacing={0} align="stretch" py={1}>
          {searchResults.map((business) => (
            <BusinessOption
              key={business.place_id || business.name}
              business={business}
              onSelect={handleSelectBusiness}
            />
          ))}
        </VStack>
      )}
    </Box>
  ) : null;

  const submitButton = (
    <Button
      type="submit"
      h={INPUT_HEIGHT}
      minH={INPUT_HEIGHT}
      maxH={INPUT_HEIGHT}
      px={6}
      borderRadius="md"
      color="white"
      fontWeight="700"
      fontSize="sm"
      whiteSpace="nowrap"
      flexShrink={0}
      w={isDesktop ? 'auto' : 'full'}
      isLoading={isAnalyzing}
      isDisabled={!canAnalyze}
      rightIcon={<Icon as={FaArrowRight} boxSize={3.5} />}
      bg="#0066FF"
      _hover={{ bg: '#0052CC' }}
      _active={{ bg: '#0047B3' }}
      _disabled={{ opacity: 0.55, cursor: 'not-allowed' }}
    >
      Continue
    </Button>
  );

  return (
    <>
      <Box as="form" onSubmit={handleAnalyze} w="full" display="flex" justifyContent="center">
        <VStack
          ref={searchWrapperRef}
          w="full"
          maxW={{ base: '100%', sm: '520px', md: '560px' }}
          spacing={2}
          align="stretch"
        >
          {isDesktop ? (
            <HStack spacing={2} align="stretch">
              {searchInput}
              {submitButton}
            </HStack>
          ) : (
            <VStack spacing={2} align="stretch">
              {searchInput}
              {submitButton}
            </VStack>
          )}

          {dropdownPanel}

          {(resolveError || error) && (
            <Text fontSize="xs" color={isHero ? 'red.200' : 'red.500'} lineHeight="short">
              {resolveError || error}
            </Text>
          )}

          {notFound && (
            <VStack spacing={2} align="stretch" mt={2} data-testid="landing-notfound-retry">
              <HStack spacing={2}>
                <Input
                  size="sm"
                  placeholder="Neighborhood (optional)"
                  value={retryNeighborhood}
                  maxLength={80}
                  onChange={(e) => setRetryNeighborhood(e.target.value)}
                  bg="white"
                />
                <Input
                  size="sm"
                  placeholder="City, State"
                  value={retryCity}
                  maxLength={80}
                  onChange={(e) => setRetryCity(e.target.value)}
                  bg="white"
                />
              </HStack>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleRetryWithHints}
                isLoading={isAnalyzing}
                isDisabled={!retryNeighborhood.trim() && !retryCity.trim()}
                data-testid="landing-notfound-retry-button"
              >
                Search again with location
              </Button>
            </VStack>
          )}
        </VStack>
      </Box>

      <ProspectMarketAnalysisModal
        isOpen={isOpen}
        onClose={closeAnalysis}
        anchorBusiness={anchorBusiness}
        marketKeyword={marketKeyword}
        competitors={competitors}
        analysisProgress={analysisProgress}
        analysisStep={analysisStep}
        email={email}
        onEmailChange={(value) => {
          setEmail(value);
          if (emailError) setEmailError('');
        }}
        emailError={emailError}
        onConfirm={handleConfirmReport}
        isConfirming={isSubmitting}
      />
    </>
  );
}

export default ProspectGmbSignupForm;
